import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Person as PersonIcon,
  Group as GroupIcon,
  Delete as DeleteIcon,
  FolderShared as FolderSharedIcon,
  Badge as BadgeIcon,
} from "@mui/icons-material";
import type {
  GroupCoverage,
  ShareCoverageResponse,
  ShareLevel,
  SharesResponse,
  UserCoverage,
} from "src/api/shares";
import RetryableError from "src/views/assistant/projects/components/RetryableError";
import { labelForLevel } from "./PermissionSelect";
import type { InheritedAccessCopy } from "./copy";
import CoverageChip, {
  groupChipLabel,
  groupHasGaps,
  userChipLabel,
  userCoverageState,
} from "./CoverageChip";
import { hiddenDatasetLabel } from "./CoverageDatasetsBlock";

/** A dataset grant offered inline from a recipient's coverage detail. */
export interface DatasetShareTarget {
  datasetId: number;
  kind: "user" | "group";
  id: number;
}

export interface PendingRevoke {
  kind: "user" | "group";
  id: number;
  label: string;
}

interface AccessListProps {
  shares: SharesResponse | undefined;
  isLoading: boolean;
  /** Set when the shares query failed. Rendered as a retryable error — the
   *  empty-access text below is reserved for a load that actually succeeded
   *  and found nothing, not a load that never returned an answer. */
  error?: unknown;
  onRetry?: () => void;
  tid: string;
  emptyText: string;
  /** Absent for resource types that cannot inherit folder access, which is
   *  also why the backend sends them no inherited rows. */
  inheritedCopy?: InheritedAccessCopy;
  onRevoke: (revoke: PendingRevoke) => void;
  revokeDisabled: boolean;
  /** Per-recipient dataset coverage (agents). Present once computed; the
   *  rows render without it and the chips appear when it lands. */
  coverage?: ShareCoverageResponse;
  onShareDataset?: (target: DatasetShareTarget) => void;
  /** The inline share in flight, as `${kind}-${id}-${datasetId}`. */
  sharingKey?: string | null;
}

// Role grants go through the permissions API, not the share routes, so their
// rows carry no remove control.
const ROLE_SHARE_CAPTION = "Everyone with this role";

const shareKey = (t: DatasetShareTarget) => `${t.kind}-${t.id}-${t.datasetId}`;

const AccessList: React.FC<AccessListProps> = ({
  shares,
  isLoading,
  error,
  onRetry,
  tid,
  emptyText,
  inheritedCopy,
  onRevoke,
  revokeDisabled,
  coverage,
  onShareDataset,
  sharingKey,
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [gapsOnly, setGapsOnly] = useState(false);
  const userCoverage = useMemo(
    () => new Map((coverage?.users ?? []).map((u) => [u.user_id, u])),
    [coverage],
  );
  const groupCoverage = useMemo(
    () => new Map((coverage?.groups ?? []).map((g) => [g.group_id, g])),
    [coverage],
  );
  const datasetsTotal = coverage?.datasets_total ?? 0;
  const toggle = (key: string) =>
    setExpanded((current) => (current === key ? null : key));

  const userRows = (shares?.user_shares ?? []).filter((share) => {
    if (!gapsOnly || !coverage) return true;
    const row = userCoverage.get(share.user_id);
    return row ? userCoverageState(row, datasetsTotal) !== "full" : false;
  });
  const groupRows = (shares?.group_shares ?? []).filter((share) => {
    if (!gapsOnly || !coverage) return true;
    const row = groupCoverage.get(share.group_id);
    return row ? groupHasGaps(row) : false;
  });

  // Role grants have no coverage row, so the gaps filter hides them.
  const roleShares = shares?.role_shares ?? [];
  const roleRows = gapsOnly && coverage ? [] : roleShares;

  const directCount =
    (shares?.user_shares.length ?? 0) + (shares?.group_shares.length ?? 0);
  // Ignore inherited rows without copy to describe them, so the counts below
  // can never promise rows the render skips.
  const inherited = inheritedCopy ? shares : undefined;
  const inheritedUsers = inherited?.inherited_user_shares ?? [];
  const inheritedGroups = inherited?.inherited_group_shares ?? [];
  const inheritedRoles = inherited?.inherited_role_shares ?? [];
  const inheritedCount =
    inheritedUsers.length + inheritedGroups.length + inheritedRoles.length;

  if (isLoading) {
    return (
      <Box>
        {[0, 1].map((i) => (
          <Skeleton key={i} variant="rounded" height={48} sx={{ my: 0.5 }} />
        ))}
      </Box>
    );
  }
  if (error != null) {
    return (
      <RetryableError
        message="Couldn't load who has access."
        onRetry={onRetry}
        testId={`${tid}-access-list-error`}
      />
    );
  }
  // Folder-derived access counts as access: a dataset shared only through its
  // folder must not be described as shared with nobody.
  if (directCount + roleShares.length + inheritedCount === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {emptyText}
      </Typography>
    );
  }
  return (
    <>
      {coverage && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
          data-testid={`${tid}-coverage-header`}
        >
          <Typography variant="caption" color="text.secondary">
            {directCount} people and groups · {coverage.people_total} people in
            all
          </Typography>
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Switch
                size="small"
                checked={gapsOnly}
                onChange={(e) => setGapsOnly(e.target.checked)}
                inputProps={
                  {
                    "data-testid": `${tid}-coverage-gaps-only`,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
            label={
              <Typography variant="caption">
                Show only people with gaps
              </Typography>
            }
          />
        </Stack>
      )}
      <List dense disablePadding data-testid={`${tid}-access-list`}>
        {userRows.map((share) => {
          const row = userCoverage.get(share.user_id);
          const key = `user-${share.user_id}`;
          const displayName = share.name || share.email;
          return (
            <React.Fragment key={key}>
              <ListItem
                data-testid={`${tid}-user-item-${share.user_id}`}
                sx={{ px: 0 }}
              >
                <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
                <ListItemText
                  primary={displayName}
                  secondaryTypographyProps={{ component: "div" }}
                  secondary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption">{share.email}</Typography>
                      <Chip
                        size="small"
                        label={labelForLevel(share.permission_level)}
                        variant="outlined"
                      />
                      {row && (
                        <CoverageChip
                          label={userChipLabel(row, datasetsTotal)}
                          state={userCoverageState(row, datasetsTotal)}
                          expanded={expanded === key}
                          onToggle={() => toggle(key)}
                          tid={`${tid}-coverage-chip-user-${share.user_id}`}
                        />
                      )}
                    </Stack>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    data-testid={`${tid}-user-remove-${share.user_id}`}
                    onClick={() =>
                      onRevoke({
                        kind: "user",
                        id: share.user_id,
                        label: displayName,
                      })
                    }
                    disabled={revokeDisabled}
                    aria-label={`Remove access for ${displayName}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              {row && (
                <Collapse in={expanded === key} unmountOnExit>
                  <UserCoverageDetail
                    row={row}
                    displayName={displayName}
                    datasetsTotal={datasetsTotal}
                    tid={`${tid}-coverage-detail-user-${share.user_id}`}
                    onShareDataset={onShareDataset}
                    sharingKey={sharingKey}
                  />
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
        {groupRows.map((share) => {
          const row = groupCoverage.get(share.group_id);
          const key = `group-${share.group_id}`;
          return (
            <React.Fragment key={key}>
              <ListItem
                data-testid={`${tid}-group-item-${share.group_id}`}
                sx={{ px: 0 }}
              >
                <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                  <GroupIcon fontSize="small" />
                </Avatar>
                <ListItemText
                  primary={share.group_name}
                  secondaryTypographyProps={{ component: "div" }}
                  secondary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={labelForLevel(share.permission_level)}
                        variant="outlined"
                      />
                      {share.is_system && (
                        <Chip
                          size="small"
                          label="Everyone"
                          variant="outlined"
                        />
                      )}
                      {row && (
                        <CoverageChip
                          label={groupChipLabel(row)}
                          state={groupHasGaps(row) ? "partial" : "full"}
                          expanded={expanded === key}
                          onToggle={() => toggle(key)}
                          tid={`${tid}-coverage-chip-group-${share.group_id}`}
                        />
                      )}
                    </Stack>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    data-testid={`${tid}-group-remove-${share.group_id}`}
                    onClick={() =>
                      onRevoke({
                        kind: "group",
                        id: share.group_id,
                        label: share.group_name,
                      })
                    }
                    disabled={revokeDisabled}
                    aria-label={`Remove access for ${share.group_name}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              {row && (
                <Collapse in={expanded === key} unmountOnExit>
                  <GroupCoverageDetail
                    row={row}
                    groupName={share.group_name}
                    tid={`${tid}-coverage-detail-group-${share.group_id}`}
                    onShareDataset={onShareDataset}
                    sharingKey={sharingKey}
                  />
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
        {roleRows.map((share) => (
          <ListItem
            key={`role-${share.role_id}`}
            data-testid={`${tid}-role-item-${share.role_id}`}
            sx={{ px: 0 }}
          >
            <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
              <BadgeIcon fontSize="small" />
            </Avatar>
            <ListItemText
              primary={share.role_name}
              secondaryTypographyProps={{ component: "div" }}
              secondary={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption">
                    {ROLE_SHARE_CAPTION}
                  </Typography>
                  <Chip
                    size="small"
                    label={labelForLevel(share.permission_level)}
                    variant="outlined"
                  />
                </Stack>
              }
            />
          </ListItem>
        ))}
        {inheritedCopy && (
          <>
            {inheritedUsers.map((share) => {
              // A folder owner can also hold an explicit grant on that folder, so
              // the same user_id arrives twice — the rows need distinct ids.
              const rowId = share.via_folder_ownership
                ? `${share.user_id}-owner`
                : `${share.user_id}`;
              return (
                <InheritedRow
                  key={`inherited-user-${rowId}`}
                  tid={`${tid}-inherited-user-${rowId}`}
                  icon={<PersonIcon fontSize="small" />}
                  primary={share.name || share.email}
                  caption={share.email}
                  level={share.permission_level}
                  folderName={share.source_folder_name}
                  viaOwnership={share.via_folder_ownership}
                  copy={inheritedCopy}
                />
              );
            })}
            {inheritedGroups.map((share) => (
              <InheritedRow
                key={`inherited-group-${share.group_id}`}
                tid={`${tid}-inherited-group-${share.group_id}`}
                icon={<GroupIcon fontSize="small" />}
                primary={share.group_name}
                level={share.permission_level}
                folderName={share.source_folder_name}
                isSystem={share.is_system}
                copy={inheritedCopy}
              />
            ))}
            {inheritedRoles.map((share) => (
              <InheritedRow
                key={`inherited-role-${share.role_id}`}
                tid={`${tid}-inherited-role-${share.role_id}`}
                icon={<BadgeIcon fontSize="small" />}
                primary={share.role_name}
                caption={inheritedCopy.roleCaption}
                level={share.permission_level}
                folderName={share.source_folder_name}
                copy={inheritedCopy}
              />
            ))}
          </>
        )}
      </List>
    </>
  );
};

const hiddenCountLabel = (n: number) =>
  n === 1 ? hiddenDatasetLabel : `${n} datasets you cannot read`;

interface UserCoverageDetailProps {
  row: UserCoverage;
  displayName: string;
  datasetsTotal: number;
  tid: string;
  onShareDataset?: (target: DatasetShareTarget) => void;
  sharingKey?: string | null;
}

/** The datasets one person is missing, with an inline grant where the
 *  caller manages the dataset and the owner to ask otherwise. */
const UserCoverageDetail: React.FC<UserCoverageDetailProps> = ({
  row,
  displayName,
  datasetsTotal,
  tid,
  onShareDataset,
  sharingKey,
}) => (
  <Box sx={{ pl: 6, pb: 1.5 }} data-testid={tid}>
    {userCoverageState(row, datasetsTotal) === "none" && (
      <Typography variant="caption" color="text.secondary" display="block">
        {displayName} can&apos;t read any of this agent&apos;s {datasetsTotal}{" "}
        datasets yet. They still see the agent, with a line telling them to ask
        you.
      </Typography>
    )}
    {row.missing.map((dataset) => {
      const target: DatasetShareTarget = {
        datasetId: dataset.dataset_id,
        kind: "user",
        id: row.user_id,
      };
      const busy = sharingKey === shareKey(target);
      return (
        <Stack
          key={dataset.dataset_id}
          direction="row"
          spacing={1}
          alignItems="center"
          data-testid={`${tid}-missing-${dataset.dataset_id}`}
        >
          {dataset.caller_can_manage && onShareDataset ? (
            <Button
              size="small"
              variant="text"
              disabled={sharingKey != null}
              onClick={() => onShareDataset(target)}
              data-testid={`${tid}-share-${dataset.dataset_id}`}
            >
              {busy ? "Sharing…" : `Share ${dataset.name} with ${displayName}`}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {dataset.name}, ask its owner, {dataset.owner_name ?? "unknown"}
            </Typography>
          )}
        </Stack>
      );
    })}
    {row.hidden_missing_count > 0 && (
      <Typography variant="caption" color="text.secondary" display="block">
        {hiddenCountLabel(row.hidden_missing_count)}
      </Typography>
    )}
  </Box>
);

interface GroupCoverageDetailProps {
  row: GroupCoverage;
  groupName: string;
  tid: string;
  onShareDataset?: (target: DatasetShareTarget) => void;
  sharingKey?: string | null;
}

/** Per dataset, how many of the group's members are short of it, with an
 *  inline group grant where the caller manages the dataset. */
const GroupCoverageDetail: React.FC<GroupCoverageDetailProps> = ({
  row,
  groupName,
  tid,
  onShareDataset,
  sharingKey,
}) => (
  <Box sx={{ pl: 6, pb: 1.5 }} data-testid={tid}>
    {row.members_evaluated < row.member_count && (
      <Typography variant="caption" color="text.secondary" display="block">
        {row.members_evaluated} of {row.member_count} members evaluated
      </Typography>
    )}
    {row.gaps.map((gap, index) => {
      const hidden = gap.dataset_id === null;
      const gapId = hidden ? `hidden-${index}` : `${gap.dataset_id}`;
      const target: DatasetShareTarget | null = hidden
        ? null
        : { datasetId: gap.dataset_id!, kind: "group", id: row.group_id };
      const busy = target !== null && sharingKey === shareKey(target);
      const who =
        gap.sample_names.length > 0 ? ` (${gap.sample_names.join(", ")})` : "";
      return (
        <Stack
          key={gapId}
          direction="row"
          spacing={1}
          alignItems="center"
          data-testid={`${tid}-gap-${gapId}`}
        >
          <Typography variant="caption" color="text.secondary">
            {hidden ? hiddenDatasetLabel : gap.name}: {gap.members_short} of{" "}
            {row.members_evaluated} can&apos;t read it{who}
          </Typography>
          {target && gap.caller_can_manage && onShareDataset ? (
            <Button
              size="small"
              variant="text"
              disabled={sharingKey != null}
              onClick={() => onShareDataset(target)}
              data-testid={`${tid}-share-${gapId}`}
            >
              {busy ? "Sharing…" : `Share ${gap.name} with ${groupName}`}
            </Button>
          ) : (
            !hidden && (
              <Typography variant="caption" color="text.secondary">
                ask its owner, {gap.owner_name ?? "unknown"}
              </Typography>
            )
          )}
        </Stack>
      );
    })}
  </Box>
);

interface InheritedRowProps {
  tid: string;
  icon: React.ReactNode;
  primary: string;
  caption?: string;
  level: ShareLevel;
  folderName: string;
  /** Access comes from OWNING the folder, so there is no share to revoke — it
   *  ends by moving the dataset out or reassigning the folder. */
  viaOwnership?: boolean;
  /** System group ("Everyone"), same flag the direct group rows carry. */
  isSystem?: boolean;
  copy: InheritedAccessCopy;
}

/** A row for access that comes from the folder, so it has no grant to revoke
 *  here — revoking means removing the folder share or moving the dataset out. */
const InheritedRow: React.FC<InheritedRowProps> = ({
  tid,
  icon,
  primary,
  caption,
  level,
  folderName,
  viaOwnership,
  isSystem,
  copy,
}) => (
  <ListItem sx={{ px: 0 }} data-testid={tid}>
    <Avatar sx={{ mr: 2, width: 32, height: 32 }}>{icon}</Avatar>
    <ListItemText
      primary={primary}
      secondary={
        <Stack direction="row" spacing={1} alignItems="center">
          {caption && <Typography variant="caption">{caption}</Typography>}
          <Chip size="small" label={labelForLevel(level)} variant="outlined" />
          {isSystem && (
            <Chip size="small" label="Everyone" variant="outlined" />
          )}
          <Tooltip
            describeChild
            title={
              viaOwnership
                ? copy.ownershipTooltip(folderName)
                : copy.grantTooltip(folderName)
            }
          >
            {/* tabIndex: a Chip with no onClick renders a plain div, which is
                not focusable — without it the tooltip (the only place the
                no-revoke remediation is stated) never opens from the keyboard. */}
            <Chip
              tabIndex={0}
              size="small"
              icon={<FolderSharedIcon fontSize="small" />}
              label={
                viaOwnership
                  ? copy.ownershipChipLabel(folderName)
                  : copy.grantChipLabel(folderName)
              }
              variant="outlined"
              color="info"
            />
          </Tooltip>
        </Stack>
      }
    />
  </ListItem>
);

export default AccessList;
